import type { Integration, IntegrationViewKind } from '../../../core/domain/integration';

/*
 * The pure logic behind "your plan is full" — shown BEFORE the click.
 *
 * The server already refuses correctly (services/integrations/…/ceilings.go):
 * it counts the org's integrations of each KIND and compares to the ceiling in
 * the signed grant. What it cannot do is stop someone filling in a whole form,
 * password included, before finding out. The browser holds every fact needed to
 * answer first — the ceilings ride the plan stream, the catalog says which kind
 * each type is, the roster says which kind each running integration is — so this
 * counts and compares locally, and the Add button + the type picker read it.
 *
 * Pulled out of the two views for the same reason integration-form-model.ts is:
 * the one rule that matters here — AN ABSENT CEILING IS UNLIMITED, NEVER ZERO —
 * is exactly the kind of thing a careless `?? 0` inverts, and inverting it tells
 * the highest-paying customers they own nothing. bench/integration-limits.test.ts
 * pins it without a browser.
 *
 * The server stays the authority; a stale roster or plan is corrected by its
 * refusal, exactly as docs/gated-ui.md prescribes.
 */

/** The three kinds a tier caps separately — one viewport, one behaviour, one
 *  price line. 'unspecified' is uncapped on the server too. */
export type Kind = 'table' | 'topic' | 'endpoint';
export const KINDS: readonly Kind[] = ['table', 'topic', 'endpoint'];

/** The capability the ceilings hang off, and the limit key each kind is capped
 *  under. The keys are the wire strings the api-gateway derives from the Limit
 *  enum; the bench test asserts they match @vitesse/trust's `Limit` constants,
 *  so this module can stay free of runtime imports (it is bundled into Node). */
export const CAPABILITY = 'it_integrations';
export const LIMIT_KEY: Readonly<Record<Kind, string>> = {
  table: 'max_table',
  topic: 'max_topic',
  endpoint: 'max_endpoint',
};

/** The word the server's own refusal uses for each kind (ceilings.go kindLabel),
 *  so the hint before the click and the error after it name the same thing. */
export const KIND_LABEL: Readonly<Record<Kind, string>> = {
  table: 'database',
  topic: 'broker',
  endpoint: 'backend',
};

export function isKind(v: IntegrationViewKind): v is Kind {
  return v === 'table' || v === 'topic' || v === 'endpoint';
}

/** How many of each kind the org is running. Every integration in the roster
 *  counts — the server counts running AND connecting, because one retrying
 *  against a dead host still occupies the slot the customer bought. */
export function usedByKind(integrations: readonly Pick<Integration, 'viewKind'>[]): Record<Kind, number> {
  const out: Record<Kind, number> = { table: 0, topic: 0, endpoint: 0 };
  for (const i of integrations) if (isKind(i.viewKind)) out[i.viewKind]++;
  return out;
}

/** What the plan says about one kind. `limit` undefined is UNLIMITED. */
export interface Room {
  kind: Kind;
  used: number;
  limit: number | undefined;
  /** True only when a FINITE ceiling has been reached. Unlimited is never full. */
  full: boolean;
}

/** The reader shape `Session.limit` has — injected so this stays testable with
 *  a plain function and never touches the DOM. */
export type LimitReader = (feature: string, key: string) => number | undefined;

export function roomFor(kind: Kind, used: number, limitOf: LimitReader): Room {
  const limit = limitOf(CAPABILITY, LIMIT_KEY[kind]);
  // `!== undefined` on purpose. A ceiling of 0 IS full ("this tier includes no
  // databases"), and `undefined` is unlimited. `if (limit)` would merge the two
  // into "unlimited" and offer a type the server then refuses; `?? 0` would merge
  // them the other way and refuse the tiers that bought the most.
  return { kind, used, limit, full: limit !== undefined && used >= limit };
}

export function rooms(integrations: readonly Pick<Integration, 'viewKind'>[], limitOf: LimitReader): Record<Kind, Room> {
  const used = usedByKind(integrations);
  return {
    table: roomFor('table', used.table, limitOf),
    topic: roomFor('topic', used.topic, limitOf),
    endpoint: roomFor('endpoint', used.endpoint, limitOf),
  };
}

/** The Add button locks only when there is nothing left to add of ANY kind.
 *  One unlimited or open kind keeps it live — the picker then does the finer
 *  work of greying the kinds that are full. */
export function everyKindFull(r: Record<Kind, Room>): boolean {
  return KINDS.every((k) => r[k].full);
}

/** The first full kind — the one the Add button's lock names when all are full. */
export function firstFull(r: Record<Kind, Room>): Room | undefined {
  return KINDS.map((k) => r[k]).find((x) => x.full);
}

/** The requirement + usage a gate evaluates for one kind — the trust layer's
 *  own quantitative axis (Requirement.consumes), so the lock and its sentence
 *  come from the same `evaluate` the rest of the UI uses, not a local copy. */
export function requirementFor(kind: Kind): { capability: string; consumes: string } {
  return { capability: CAPABILITY, consumes: LIMIT_KEY[kind] };
}
export function usageFor(room: Room): Record<string, number> {
  return { [LIMIT_KEY[room.kind]]: room.used };
}

/** The short form for a picker row: "1 of 1 in use". Only ever rendered for a
 *  FULL room, so `limit` is known to be finite here. */
export function shortHint(room: Room): string {
  return `${room.used} of ${room.limit ?? '∞'} ${KIND_LABEL[room.kind]} in use`;
}
